import React from "react";
import { Pagination } from "@sscs/types";
import {
  Card,
  CopyIcon,
  DataTable,
  DataTableColumn,
  IconButton,
  Table,
  Toast,
  TooltipIcon,
  ValueOrPlaceholder,
  useFilterSortPagination,
} from "@sscs/ui";
import {
  calculatePaginationOffset,
  getQueryStatus,
  humanReadableBytes,
} from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { useGetCliUsageQuery } from "../../services/licensing.api";
import { SiteKeysSort } from "../../types";
import styles from "./manageSiteKeys.module.scss";
import SiteKeyActions from "./SiteKeyActions";

const initSort: SiteKeysSort = {
  property: "usage",
  reverse: true,
};

const initPagination: Pagination = {
  currentPage: 1,
  itemsPerPage: 10,
};

const ManageSiteKeys = (): React.JSX.Element => {
  const canManageCliCapacity = useAllowedTo({
    permissions: [PERMISSION.MODIFY_CLI_SITE_KEY_ALLOCATIONS],
  });
  const currentOrganization = useCurrentOrganization();
  const { sort, pagination, handleSortChange, handlePaginationChange } =
    useFilterSortPagination({}, initSort, initPagination);
  const { data, isError, isFetching, isLoading, error } = useGetCliUsageQuery(
    {
      organizationId: currentOrganization?.id || "",
      sortBy: `${sort.reverse ? "-" : ""}${sort.property}`,
      offset: calculatePaginationOffset(pagination),
      limit: pagination.itemsPerPage,
    },
    { skip: !currentOrganization },
  );
  const totalSharedPoolUsedBytes =
    (data?.shared_pool.usage_bytes ?? 0) +
    (data?.shared_pool.deleted_allocations_usage_bytes ?? 0);

  const columns: DataTableColumn<SiteKeysSort["property"]>[] = [
    { id: "siteKey", label: "Site key", width: "400px" },
    {
      id: "description",
      label: "Description",
      sortProperty: "description",
      width: "100%",
      minWidth: "130px",
    },
    {
      id: "allocation",
      label: "Allocation",
      sortProperty: "allocation",
      width: "130px",
    },
    {
      id: "usage",
      label: "Used (GB)",
      sortProperty: "usage",
      width: data?.shared_pool.deleted_allocations_usage_bytes
        ? "230px"
        : "130px",
    },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      width: "90px",
      hidden: !canManageCliCapacity,
    },
  ];

  return (
    <Card.Card>
      <Card.Title as="h2">
        {`${canManageCliCapacity ? "Manage" : "Review"} Site keys`}
      </Card.Title>
      <Card.Content flush>
        <DataTable
          chrome="plain"
          columns={columns}
          sort={sort}
          onSortChange={handleSortChange}
          pagination={{
            itemCount: data?.match_count ?? 0,
            pagination,
            onChange: handlePaginationChange,
          }}
          status={getQueryStatus({
            isLoading,
            isFetching,
            isError,
            error,
            data,
          })}
        >
          <>
            <Table.Row>
              <Table.Cell>
                <div className={styles["text-with-icon"]}>
                  Shared pool
                  <TooltipIcon title="The remaining capacity after individual site key allocations." />
                </div>
              </Table.Cell>
              <Table.Cell>
                <ValueOrPlaceholder value={undefined} />
              </Table.Cell>
              <Table.Cell>
                {humanReadableBytes(
                  data?.shared_pool.allocation_bytes ?? 0,
                  2,
                  "GB",
                )}
              </Table.Cell>
              <Table.Cell>
                {humanReadableBytes(totalSharedPoolUsedBytes, 2, "GB")}
                &nbsp;(
                {data?.shared_pool.allocation_bytes
                  ? (
                      (totalSharedPoolUsedBytes * 100) /
                      data.shared_pool.allocation_bytes
                    ).toFixed(0)
                  : 0}
                %)
                {(data?.shared_pool.deleted_allocations_usage_bytes ?? 0) >
                  0 && (
                  <div className={styles["deleted-allocations"]}>
                    {humanReadableBytes(
                      data?.shared_pool.deleted_allocations_usage_bytes ?? 0,
                      2,
                      "GB",
                    )}{" "}
                    used by deleted site keys
                  </div>
                )}
              </Table.Cell>
              {canManageCliCapacity && (
                <Table.Cell align="center">
                  <ValueOrPlaceholder value={undefined} />
                </Table.Cell>
              )}
            </Table.Row>
            {data?.site_keys.map((siteKey, index) => (
              <Table.Row
                key={siteKey.id}
                className={siteKey.deleted ? styles["deleted-row"] : undefined}
              >
                <Table.Cell>
                  <div className={styles["text-with-icon"]}>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        await navigator.clipboard.writeText(siteKey.site_key);
                        Toast.show({
                          title: (
                            <>
                              <strong>{siteKey.description}</strong> site key
                              copied successfully!
                            </>
                          ),
                          type: "success",
                        });
                      }}
                      className={styles["copy-site-key"]}
                    >
                      <CopyIcon />
                    </IconButton>
                    {siteKey.site_key}
                    {siteKey.deleted && (
                      <span className={styles["deleted-key"]}>[Deleted]</span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <ValueOrPlaceholder value={siteKey.description} />
                </Table.Cell>
                <Table.Cell>
                  {humanReadableBytes(siteKey.allocation_bytes, 2, "GB")}
                </Table.Cell>
                <Table.Cell>
                  {humanReadableBytes(siteKey.usage_bytes, 2, "GB")}&nbsp;(
                  {siteKey.usage_bytes && siteKey.allocation_bytes
                    ? (
                        (siteKey.usage_bytes * 100) /
                        siteKey.allocation_bytes
                      ).toFixed(0)
                    : 0}
                  %)
                </Table.Cell>
                <AllowedTo
                  permissions={[PERMISSION.MODIFY_CLI_SITE_KEY_ALLOCATIONS]}
                >
                  <Table.Cell align="center">
                    <SiteKeyActions
                      siteKey={siteKey}
                      rowIndex={index}
                      allocationLimit={
                        (data?.shared_pool.allocation_bytes ?? 0) -
                        totalSharedPoolUsedBytes
                      }
                    />
                  </Table.Cell>
                </AllowedTo>
              </Table.Row>
            ))}
          </>
        </DataTable>
      </Card.Content>
    </Card.Card>
  );
};

export default ManageSiteKeys;
